<template>
  <div
    class="base-screen-tab-focus-view"
    :class="{ filtering: filteringApplied }"
  >
    <!-- Left panel: containing the content preview + actions -->
    <div class="left-panel">
      <!-- Containing the content preview -->
      <div class="content-preview">
        <full-text-panel
          v-if="showFullText"
          :reference="selectedReference"
        />
        <abstract-content
          v-else
          class="pt-16"
          :title="publication.title"
          :abstract="publication.abstract"
          :search-terms="highlights"
          @add-abstract="$emit('edit-field', 'abstract')"
        />

        <!-- Show List Button (hovers over the content preview in the top left corner) -->
        <rounded-action-button
          class="show-list-button"
          color="white"
          @click="$emit('show-list')"
        >
          <v-icon left>list</v-icon>
          Show List
        </rounded-action-button>
      </div>

      <!-- Containing the actions -->
      <div class="actions pa-4">
        <!-- Previous button -->
        <rounded-action-button
          color="white"
          width="126"
          :loading="loadingMore"
          :disabled="disablePreviousButton"
          @click="$emit('nav-previous')"
        >
          <v-icon left>skip_previous</v-icon>
          Previous
        </rounded-action-button>

        <div class="actions-slot-container">
          <!-- Slot to add actions -->
          <slot name="actions">Actions Slot</slot>

          <!-- Undo button -->
          <rounded-action-button
            v-if="showUndoButton"
            color="white"
            width="126"
            @click="$emit('undo')"
          >
            <v-icon left>undo</v-icon>
            Undo
          </rounded-action-button>
        </div>

        <!-- Next button -->
        <rounded-action-button
          :loading="loadingMore"
          :disabled="disableNextButton"
          color="white"
          width="126"
          @click="$emit('nav-next')"
        >
          Next
          <v-icon right>skip_next</v-icon>
        </rounded-action-button>
      </div>
    </div>

    <!-- Right panel: containing the content metadata -->
    <div class="right-panel">
      <reference-details-section
        :review="review"
        :reference="selectedReference"
        :current-researcher="currentResearcher"
        allow-edit
        @edit="$emit('edit-field')"
        @update-tags="
          $emit('update-tags', {
            reference: selectedReference,
            tags: $event,
          })
        "
        @download-full-text="onDownloadFullText"
        @delete-full-text="onDeleteFullText"
      />
    </div>
  </div>
</template>

<script>
import AbstractContent from '@/components/Common/AbstractContent';
import RoundedActionButton from '@/components/Common/RoundedActionButton';
import ReferenceDetailsSection from '@/components/References/ReferenceDetailsSection';
import FullTextPanel from '@/components/FullTextReviewPage/FullTextPanel';
import { Review, Reference, Researcher } from '@/models';
import { mapActions } from 'vuex';

export default {
  name: 'BaseScreenTabFocusView',
  components: {
    AbstractContent,
    RoundedActionButton,
    ReferenceDetailsSection,
    FullTextPanel,
  },
  props: {
    review: {
      type: Review,
      required: true,
    },
    selectedReference: {
      type: Reference,
      required: true,
    },
    currentResearcher: {
      type: Researcher,
      required: true,
    },
    showFullText: {
      type: Boolean,
      default: false,
    },
    showUndoButton: {
      type: Boolean,
      default: false,
    },
    loadingMore: {
      type: Boolean,
      default: false,
    },
    disablePreviousButton: {
      type: Boolean,
      default: false,
    },
    disableNextButton: {
      type: Boolean,
      default: false,
    },
    highlights: {
      type: Array,
      default: () => [],
    },
    filteringApplied: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'nav-previous',
    'nav-next',
    'show-list',
    'undo',
    'edit-field',
  ],
  computed: {
    publication() {
      return this.selectedReference.publication;
    },
  },
  methods: {
    ...mapActions({
      fetchDownloadUrl: 'fullTextPdf/fetchDownloadUrl',
      deleteFullTextPdf: 'fullTextPdf/delete',
    }),
    async onDownloadFullText() {
      const url = await this.fetchDownloadUrl({
        referenceId: this.selectedReference.id,
        forDownload: true,
      });
      const a = document.createElement('a');
      a.href = url;
      a.click();
    },
    onDeleteFullText() {
      this.deleteFullTextPdf(this.selectedReference.id);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~vuetify/src/styles/styles.sass';

.base-screen-tab-focus-view {
  --left-width: 1fr;
  --right-width: 400px;

  display: grid;
  grid-template-columns: var(--left-width) var(--right-width);

  &.filtering {
    --filter-bar-height: #{$screen-tab-filtering-status-bar-height};
  }
  &:not(.filtering) {
    --filter-bar-height: 0px;
  }

  --sub-page-tabs-height: calc(50px + var(--filter-bar-height));
  --footer-height: 79px;
  --max-height: calc(
    100vh - #{$page-header-height} - var(--sub-page-tabs-height) - var(--footer-height)
  );
  grid-template-rows: var(--max-height);
  overflow: hidden;

  .left-panel {
    display: grid;
    grid-template-rows: 1fr auto;

    .content-preview {
      background-color: white;
      position: relative;

      overflow: scroll;

      .show-list-button {
        position: absolute;
        top: 12px;
        left: 12px;
      }
    }

    .actions {
      display: flex;
      justify-content: space-between;
      align-items: center;

      background-color: map-get($grey, 'lighten-5');
      border-top: 1px solid map-get($grey, 'lighten-2');

      .actions-slot-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
      }
    }
  }

  .right-panel {
    border-left: 1px solid map-get($grey, 'lighten-2');
  }
}
</style>
